import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import SharedHeader from "@/components/SharedHeader";
import PricingPlans from "@/components/PricingPlans";
import { useAuth } from "@/contexts/AuthContext";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

export default function PricingPage() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated users to dashboard or paywall
  useEffect(() => {
    if (!isLoading && isAuthenticated && user) {
      if (user.subscription_status === "active") {
        navigate('/episodes');
      } else {
        navigate('/paywall');
      }
    }
  }, [isAuthenticated, isLoading, navigate, user]);

  const handleSelectPlan = () => {
    // For public pricing page, any plan selection just goes to login/signup
    navigate("/login");
  };

  return (
    <div className={cn("min-h-screen", theme.colors.backgroundCream)}>
      <SharedHeader />

      <main className="mx-auto max-w-7xl px-6 py-16 pt-24">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-black mb-6 text-gray-900 dark:text-white">
            Pay For What You Use
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
            No hidden fees. No per-seat pricing. Cancel anytime with one click.
          </p>
        </div>

        <PricingPlans
          onSelectPlan={handleSelectPlan}
          ctaText="Get Started"
        />

        <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-6">
          Subscriptions renew automatically until canceled. Cancel anytime from Profile &rarr; Billing.{" "}
          <Link to="/terms" className={cn("hover:underline", theme.text.primaryOrange)}>Terms</Link>
          {" "}&bull;{" "}
          <Link to="/privacy" className={cn("hover:underline", theme.text.primaryOrange)}>Privacy</Link>
        </p>

        {/* FAQ Section */}
        <div className={cn(
          "mt-16 p-12 rounded-2xl",
          theme.cards.base
        )}>
          <h2 className="text-3xl font-black mb-8 text-center text-gray-900 dark:text-white">
            Frequently Asked Questions
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                q: "Can I change plans later?",
                a: "Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately."
              },
              {
                q: "What happens when I cancel my subscription?",
                a: "Your subscription stops renewing and access continues through the current billing period. Canceling does not delete your account or data."
              },
              {
                q: "Do you offer refunds?",
                a: "No, we do not offer refunds. Since we charge for AI usage (chat requests and transcriptions) that cannot be refunded, all purchases are final."
              },
              {
                q: "What payment methods do you accept?",
                a: "We accept all major credit cards, debit cards, and PayPal through our secure payment processor."
              }
            ].map((item) => (
              <div key={item.q}>
                <h3 className="font-bold mb-2 text-gray-900 dark:text-white">
                  {item.q}
                </h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {item.a}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 pt-8 border-t-3 border-gray-900 dark:border-gray-300 text-center">
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Have more questions? Check out our <Link to="/faq" className={cn("font-semibold hover:underline", theme.text.primaryOrange)}>FAQ page</Link> or{" "}
              <a href="mailto:quiet.lens2873@fastmail.com" className={cn("font-semibold hover:underline", theme.text.primaryOrange)}>contact support</a>.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className={cn(
        "border-t-3 border-gray-900 dark:border-gray-300",
        theme.colors.cardCream,
        "mt-16"
      )}>
        <div className="mx-auto max-w-6xl px-6 py-8 text-center text-sm text-gray-600 dark:text-gray-400">
          <p>© {new Date().getFullYear()} PodWise GPT. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
